import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSocket } from "../context/SocketContext";

// ─── press logic ──────────────────────────────────────────────────────────────
function applyPress(current, key, integer = false) {
  const d = String(current ?? "0");
  if (key === "C") return "0";
  if (key === "⌫") { const n = d.slice(0, -1); return n === "" ? "0" : n; }
  if (key === "." && integer) return d;
  if (key === "." && d.includes(".")) return d;
  let next = d === "0" && key !== "." ? key : d + key;
  if (!integer && next.includes(".")) {
    const [, dec] = next.split(".");
    if (dec && dec.length > 2) return d;
  }
  return next;
}

// ─── FieldCard ────────────────────────────────────────────────────────────────
function FieldCard({ label, value, suffix, active, onActivate, disabled }) {
  return (
    <div onPointerDown={(e) => { e.preventDefault(); if (!disabled) onActivate(); }}
      style={{
        flex: 1, minWidth: 0, border: `2px solid ${active ? "var(--brand-red)" : "var(--border)"}`,
        borderRadius: "var(--radius-sm)", padding: "7px 8px", textAlign: "center",
        background: active ? "#fff5f5" : "#fff", cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1, userSelect: "none", touchAction: "manipulation",
        transition: "border-color .15s, background .15s",
      }}>
      <div style={{ fontSize: 9, fontWeight: 700, opacity: 0.5, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, lineHeight: 1.2, color: active ? "var(--brand-red)" : "var(--text)", whiteSpace: "nowrap" }}>
        {value}{suffix}
      </div>
    </div>
  );
}

// ─── NumPad ───────────────────────────────────────────────────────────────────
function Pad({ onPress, integer }) {
  const rows = [["7","8","9"],["4","5","6"],["1","2","3"],[integer ? null : ".","0","⌫"]];
  const bg = (k) => k === "⌫" ? "#fff0f2" : "#fff";
  const col = (k) => k === "⌫" ? "var(--brand-red)" : "var(--text)";
  const pd = (k) => (e) => { if (!k) return; e.preventDefault(); e.currentTarget.style.background = "#e8e8e8"; onPress(k); };
  const pu = (k) => (e) => { if (k) e.currentTarget.style.background = bg(k); };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4 }}>
      {rows.flat().map((k, i) => (
        <button key={i} type="button" onPointerDown={pd(k)} onPointerUp={pu(k)} onPointerLeave={pu(k)}
          style={{
            padding: "11px 0", fontSize: k === "⌫" ? 17 : 20, fontWeight: 700,
            border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
            background: k ? bg(k) : "transparent", borderColor: k ? undefined : "transparent",
            color: k ? col(k) : "transparent", cursor: k ? "pointer" : "default",
            pointerEvents: k ? undefined : "none", touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent", userSelect: "none",
          }}>{k === "⌫" ? "⌫" : k ?? ""}</button>
      ))}
      <button type="button" onPointerDown={pd("C")} onPointerUp={pu("C")} onPointerLeave={pu("C")}
        style={{
          gridColumn: "1/-1", padding: "9px 0", fontSize: 12, fontWeight: 700, letterSpacing: 1,
          border: "1px solid var(--border)", borderRadius: "var(--radius-sm)",
          background: "#ffe4e4", color: "var(--brand-red)", cursor: "pointer",
          touchAction: "manipulation", WebkitTapHighlightColor: "transparent",
        }}>BORRAR</button>
    </div>
  );
}

// ─── PopupCaja ────────────────────────────────────────────────────────────────
function PopupCaja({ msg, onClose, onDone }) {
  const defaultName = msg.nickname || msg.uniqueId || String(msg.userId || "");
  const [label, setLabel] = useState(defaultName);
  const [mergeMode, setMergeMode] = useState(false);
  const [cajas, setCajas] = useState([]);
  const [mergeCaja, setMergeCaja] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Cargar cajas existentes cuando activa el modo fusión
  useEffect(() => {
    if (!mergeMode) return;
    fetch("/api/cajas/list-numbers").then(r => r.json()).then(setCajas).catch(() => {});
  }, [mergeMode]);

  const handle = async (tipo) => {
    setLoading(true); setErr("");
    try {
      const userId = String(msg.userId || "");
      // 1. Guardar usuario si no existe
      const payload = {
        id: userId,
        secUid: msg.secUid || null,
        displayName: msg.nickname || msg.uniqueId || userId,
        lastKnownUniqueId: msg.uniqueId || null,
        lastKnownNickname: msg.nickname || null,
        profilePictureUrl: msg.profilePictureUrl || null,
      };
      const r1 = await fetch("/api/users", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r1.ok && r1.status !== 409) throw new Error(`Error guardando usuario: ${r1.status}`);

      // 2. Asignar caja (con label y posible fusión)
      const cajaPayload = {
        tipo,
        cajaLabel: label.trim() !== defaultName && label.trim() ? label.trim() : null,
        ...(mergeMode && mergeCaja ? { mergeCaja: Number(mergeCaja) } : {}),
      };
      const r2 = await fetch(`/api/users/${encodeURIComponent(userId)}/caja`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(cajaPayload),
      });
      const body = await r2.json().catch(() => ({}));
      if (!r2.ok) throw new Error(body.error || `HTTP ${r2.status}`);
      onDone(body);
    } catch (e) { setErr(String(e.message || e)); }
    finally { setLoading(false); }
  };

  const mergedTipo = cajas.find(c => String(c.caja) === String(mergeCaja))?.cajaTipo;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: "22px 20px", width: "100%", maxWidth: 360, boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <strong style={{ fontSize: 15 }}>Añadir a caja</strong>
          <button onPointerDown={(e) => { e.preventDefault(); onClose(); }} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#999" }}>✕</button>
        </div>

        {/* Avatar */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, padding: "8px 10px", background: "#f8f8f8", borderRadius: 8 }}>
          {msg.profilePictureUrl && <img src={msg.profilePictureUrl} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />}
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{msg.nickname || msg.uniqueId}</div>
            <div style={{ fontSize: 11, opacity: 0.5 }}>@{msg.uniqueId}</div>
          </div>
        </div>

        {/* Nombre personalizado */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.55, display: "block", marginBottom: 5 }}>Nombre en etiqueta</label>
          <input
            value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Nombre para la etiqueta…"
            style={{ width: "100%", padding: "9px 12px", border: "1px solid #ddd", borderRadius: 10, fontSize: 14, boxSizing: "border-box", fontWeight: 600 }}
          />
          {label.trim() !== defaultName && label.trim() && (
            <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>Nombre TikTok: {defaultName}</div>
          )}
        </div>

        {/* Toggle fusión */}
        <div style={{ marginBottom: 14 }}>
          <button onPointerDown={e => { e.preventDefault(); setMergeMode(v => !v); setMergeCaja(""); }}
            style={{ border: "1px solid #ddd", borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: mergeMode ? "#fff7ed" : "#f9fafb", color: mergeMode ? "#c2410c" : "#555", width: "100%" }}>
            {mergeMode ? "✕ Cancelar fusión" : "🔗 Fusionar con caja existente"}
          </button>
        </div>

        {/* Selector fusión */}
        {mergeMode && (
          <div style={{ marginBottom: 14, padding: "10px 12px", background: "#fff7ed", borderRadius: 10, border: "1px solid #fed7aa" }}>
            <label style={{ fontSize: 12, fontWeight: 700, opacity: 0.7, display: "block", marginBottom: 6 }}>Seleccionar caja</label>
            <select value={mergeCaja} onChange={e => setMergeCaja(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}>
              <option value="">— Elige una caja —</option>
              {cajas.map(c => (
                <option key={c.caja} value={c.caja}>
                  Caja {c.caja} ({c.cajaTipo}) — {c.label}
                </option>
              ))}
            </select>
            {mergeCaja && <div style={{ fontSize: 11, marginTop: 6, opacity: 0.6 }}>Se añadirá a la caja {mergeCaja} como <strong>{mergedTipo}</strong></div>}
          </div>
        )}

        {err && <div style={{ color: "crimson", fontSize: 12, marginBottom: 10, textAlign: "center" }}>{err}</div>}

        {/* Botones acción */}
        {mergeMode ? (
          <button className="btn btn-primary" disabled={loading || !mergeCaja}
            onPointerDown={e => { e.preventDefault(); handle(mergedTipo || "recoger"); }}
            style={{ width: "100%", padding: "14px 0", fontSize: 15, fontWeight: 800 }}>
            {loading ? "Guardando…" : `🔗 Fusionar en caja ${mergeCaja}`}
          </button>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <button className="btn btn-primary" disabled={loading}
              onPointerDown={(e) => { e.preventDefault(); handle("enviar"); }}
              style={{ padding: "16px 0", fontSize: 15, fontWeight: 800, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 24 }}>📦</span>Enviar
            </button>
            <button className="btn btn-outline" disabled={loading}
              onPointerDown={(e) => { e.preventDefault(); handle("recoger"); }}
              style={{ padding: "16px 0", fontSize: 15, fontWeight: 800, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <span style={{ fontSize: 24 }}>🏪</span>Recoger
            </button>
          </div>
        )}
        {loading && <div style={{ textAlign: "center", marginTop: 12, opacity: 0.6, fontSize: 13 }}>Guardando…</div>}
      </div>
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export default function Directo() {
  const { socket, liveUser, mostrarProducto, ocultarProducto } = useSocket();

  const [modo, setModo] = useState("venta");
  const [precio, setPrecio] = useState("0");
  const [cantidad, setCantidad] = useState("1");
  const [duracion, setDuracion] = useState("30");
  const [minBid, setMinBid] = useState("0");
  const [campo, setCampo] = useState("precio");

  const [producto, setProducto] = useState(null);
  const [scanQ, setScanQ] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanErr, setScanErr] = useState("");
  const [enPantalla, setEnPantalla] = useState(false);
  const scanRef = useRef(null);

  const [ventaState, setVentaState] = useState(null);
  const [subastaState, setSubastaState] = useState({ active: false, leaderboard: [] });
  const [subastaResult, setSubastaResult] = useState(null);
  const [confirmStatus, setConfirmStatus] = useState(null);
  const [lastWinners, setLastWinners] = useState([]);
  const [now, setNow] = useState(Date.now());

  // Chat
  const [messages, setMessages] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [popupMsg, setPopupMsg] = useState(null);
  const [toastMsg, setToastMsg] = useState("");
  const chatRef = useRef(null);
  const autoScrollRef = useRef(true);
  const programmaticScrollRef = useRef(false);
  const [chatAutoScroll, setChatAutoScroll] = useState(true);
  const [chatNewCount, setChatNewCount] = useState(0);

  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 250); return () => clearInterval(id); }, []);

  // Cargar IDs guardados
  useEffect(() => {
    fetch("/api/users/ids").then(r => r.json()).then(ids => setSavedIds(new Set(ids))).catch(() => {});
  }, []);

  // Socket listeners
  useEffect(() => {
    const s = socket.current;
    if (!s) return;
    const onVS = (st) => { setVentaState(st); if (st.winners?.length) setLastWinners(st.winners); };
    const onSS = (st) => setSubastaState(st);
    const onEnded = (p) => { setSubastaResult(p); setConfirmStatus(null); };
    const onConfirm = (p) => setConfirmStatus(p);
    const onChat = (msg) => {
      setMessages(prev => { const next = [...prev, msg]; return next.length > 200 ? next.slice(-200) : next; });
      requestAnimationFrame(() => {
        if (autoScrollRef.current) {
          const el = chatRef.current;
          if (!el) return;
          programmaticScrollRef.current = true;
          el.scrollTop = el.scrollHeight;
          requestAnimationFrame(() => { programmaticScrollRef.current = false; });
        } else {
          setChatNewCount(c => c + 1);
        }
      });
    };
    s.on("venta:state", onVS); s.on("subasta:state", onSS);
    s.on("subasta:ended", onEnded); s.on("subasta:confirm:result", onConfirm); s.on("subasta:confirmed", onConfirm);
    s.on("chat", onChat);
    return () => {
      s.off("venta:state", onVS); s.off("subasta:state", onSS); s.off("subasta:ended", onEnded);
      s.off("subasta:confirm:result", onConfirm); s.off("subasta:confirmed", onConfirm); s.off("chat", onChat);
    };
  }, [socket]);

  useEffect(() => { setCampo(modo === "venta" ? "precio" : "minBid"); }, [modo]);

  const isActive = modo === "venta" ? !!ventaState?.active : !!subastaState?.active;

  const setterFor = useCallback((c) => {
    if (c === "precio") return setPrecio;
    if (c === "cantidad") return setCantidad;
    if (c === "duracion") return setDuracion;
    if (c === "minBid") return setMinBid;
    return () => {};
  }, []);

  const isInteger = (c) => c === "cantidad" || c === "duracion";

  const press = useCallback((key) => {
    if (isActive) return;
    setterFor(campo)(prev => applyPress(prev, key, isInteger(campo)));
  }, [campo, isActive, setterFor]);

  const handleStart = useCallback(() => {
    if (!liveUser || isActive || !socket.current) return;
    if (modo === "venta") {
      setLastWinners([]);
      socket.current.emit("venta:start", { uniqueId: liveUser, price: Number(precio) || 0, qty: Number(cantidad) || 1, durationSeconds: Number(duracion) || 30, articulo: producto?.articulo || null });
    } else {
      setSubastaResult(null); setConfirmStatus(null);
      socket.current.emit("subasta:start", { uniqueId: liveUser, durationSeconds: Number(duracion) || 30, minBid: Number(minBid) || 0, articulo: producto?.articulo || null });
    }
  }, [liveUser, isActive, modo, precio, cantidad, duracion, minBid, producto, socket]);

  useEffect(() => {
    const handler = (e) => {
      if (document.activeElement === scanRef.current) return;
      if (e.key >= "0" && e.key <= "9") { e.preventDefault(); press(e.key); }
      else if (e.key === "." || e.key === ",") { e.preventDefault(); press("."); }
      else if (e.key === "Backspace") { e.preventDefault(); press("⌫"); }
      else if (e.key === "Delete" || e.key === "Escape") { e.preventDefault(); press("C"); }
      else if (e.key === "Enter") { e.preventDefault(); handleStart(); }
      else if (e.key === "Tab") {
        e.preventDefault();
        const fields = modo === "venta" ? ["precio","cantidad","duracion"] : ["minBid","duracion"];
        const idx = fields.indexOf(campo);
        setCampo(fields[(idx + 1) % fields.length]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press, modo, campo, isActive, handleStart]);

  const buscar = async (q) => {
    const code = q.trim(); if (!code) return;
    setScanLoading(true); setScanErr(""); setProducto(null); setEnPantalla(false);
    try {
      const res = await fetch(`/api/productos/buscar?q=${encodeURIComponent(code)}`, { credentials: "include" });
      if (res.status === 404) { setScanErr(`No encontrado: "${code}"`); return; }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducto(data);
      if (modo === "venta" && data.pv_ini) setPrecio(String(Number(data.pv_ini).toFixed(2)));
      if (modo === "subasta" && data.pv_ini) setMinBid(String(Number(data.pv_ini).toFixed(2)));
    } catch (e) { setScanErr(String(e.message)); }
    finally { setScanLoading(false); setScanQ(""); scanRef.current?.focus(); }
  };

  const limpiarScan = () => { setProducto(null); setScanErr(""); setScanQ(""); setEnPantalla(false); ocultarProducto?.(); scanRef.current?.focus(); };

  const togglePantalla = () => {
    if (!producto) return;
    if (enPantalla) { ocultarProducto?.(); setEnPantalla(false); }
    else { const fotoProxied = producto.fotos?.[0] ? `/api/productos/foto?url=${encodeURIComponent(producto.fotos[0])}` : null; mostrarProducto?.({ ...producto, fotoProxied }); setEnPantalla(true); }
  };

  const handleReset = () => {
    if (!socket.current) return;
    if (modo === "venta") { setLastWinners([]); socket.current.emit("venta:reset", { uniqueId: liveUser }); }
    else { setSubastaResult(null); setConfirmStatus(null); socket.current.emit("subasta:reset", { uniqueId: liveUser }); }
  };

  const confirmarGanador = (winner) => socket.current?.emit("subasta:confirm", { uniqueId: liveUser, winner });

  const remSeg = useMemo(() => {
    if (!subastaState.active || !subastaState.endsAt) return 0;
    return Math.ceil(Math.max(0, subastaState.endsAt - now) / 1000);
  }, [subastaState, now]);

  const ventaRem = useMemo(() => {
    if (!ventaState?.active || !ventaState.endsAt) return 0;
    return Math.ceil(Math.max(0, ventaState.endsAt - now) / 1000);
  }, [ventaState, now]);

  const winners = ventaState?.winners || lastWinners;
  const fotoUrl = producto?.fotos?.[0] ? `/api/productos/foto?url=${encodeURIComponent(producto.fotos[0])}` : null;
  const campos = modo === "venta"
    ? [{ id: "precio", label: "Precio", value: precio, suffix: "€" }, { id: "cantidad", label: "Cantidad", value: cantidad, suffix: " ud" }, { id: "duracion", label: "Duración", value: duracion, suffix: "s" }]
    : [{ id: "minBid", label: "Puja mín.", value: minBid, suffix: "€" }, { id: "duracion", label: "Duración", value: duracion, suffix: "s" }];

  const showToast = (txt) => { setToastMsg(txt); setTimeout(() => setToastMsg(""), 2500); };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 8px 8px", display: "flex", flexDirection: "column", gap: 8, height: "calc(100dvh - 48px - 20px)", boxSizing: "border-box" }}>

      {/* Toast */}
      {toastMsg && (
        <div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", background: "#16a34a", color: "#fff", padding: "10px 22px", borderRadius: 30, fontWeight: 700, fontSize: 14, zIndex: 2000, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
          {toastMsg}
        </div>
      )}

      {/* Popup asignar caja */}
      {popupMsg && (
        <PopupCaja msg={popupMsg} onClose={() => setPopupMsg(null)} onDone={(body) => {
          setSavedIds(prev => new Set(prev).add(String(popupMsg.userId || "")));
          setPopupMsg(null);
          showToast(`✅ ${body.displayName || "Usuario"} → caja ${body.caja}`);
        }} />
      )}

      {/* Layout principal: izquierda (controles) | derecha (chat) */}
      <div className="directo-layout">

        {/* ── Columna izquierda ── */}
        <div className="directo-left">

          {/* Tabs modo */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {[{ id: "venta", icon: "🛒", label: "Venta", sub: "Precio fijo · \"mio\" gana" }, { id: "subasta", icon: "🔨", label: "Subasta", sub: "Puja más alta gana" }].map(m => (
              <button key={m.id} type="button"
                onPointerDown={(e) => { e.preventDefault(); if (!isActive) setModo(m.id); }}
                style={{ padding: "8px 10px", border: `2px solid ${modo === m.id ? "var(--brand-red)" : "var(--border)"}`, borderRadius: "var(--radius)", background: modo === m.id ? "#fff5f5" : "#fff", cursor: isActive ? "not-allowed" : "pointer", opacity: isActive && modo !== m.id ? 0.4 : 1, textAlign: "left", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>{m.icon}</span>
                <span>
                  <div style={{ fontWeight: 800, fontSize: 13, color: modo === m.id ? "var(--brand-red)" : "var(--text)" }}>{m.label}</div>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>{m.sub}</div>
                </span>
              </button>
            ))}
          </div>

          {/* Scanner */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", opacity: 0.35, pointerEvents: "none" }}>🔍</span>
              <input ref={scanRef} className="input" value={scanQ} onChange={(e) => setScanQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && buscar(scanQ)} placeholder="Escanea código..."
                disabled={scanLoading} style={{ paddingLeft: 28, width: "100%", fontSize: 13 }} />
            </div>
            <button className="btn btn-primary" onClick={() => buscar(scanQ)} disabled={scanLoading || !scanQ.trim()} style={{ fontSize: 12, padding: "8px 12px" }}>
              {scanLoading ? "…" : "Buscar"}
            </button>
            {producto && <button className="btn btn-outline" onClick={limpiarScan} style={{ fontSize: 13, padding: "8px 10px" }}>✕</button>}
          </div>

          {scanErr && <div style={{ fontSize: 12, color: "crimson", padding: "3px 8px", background: "#fff5f5", borderRadius: 6 }}>{scanErr}</div>}

          {/* Producto */}
          {producto && (
            <div style={{ border: `1.5px solid ${enPantalla ? "#16a34a" : "var(--brand-red)"}`, borderRadius: "var(--radius-sm)", background: "#fff", padding: "8px 10px" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                {fotoUrl
                  ? <img src={fotoUrl} alt="" style={{ width: 56, height: 56, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                  : <div style={{ width: 56, height: 56, borderRadius: 6, background: "#f0f0f0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, opacity: 0.4 }}>📦</div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, lineHeight: 1.3 }}>{producto.desc_corta || "Producto"}</div>
                  {(producto.marca || producto.modelo) && <div style={{ fontSize: 11, opacity: 0.6 }}>{[producto.marca, producto.modelo].filter(Boolean).join(" · ")}</div>}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                    {producto.tipo && <span style={{ fontSize: 10, background: "#f0f0f0", borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>{producto.tipo}</span>}
                    {producto.p_nuevo && <span style={{ fontSize: 11 }}>Nuevo: <strong>{Number(producto.p_nuevo).toFixed(2)}€</strong></span>}
                    {producto.pv_ini && <span style={{ fontSize: 11 }}>Venta: <strong style={{ color: "var(--brand-red)" }}>{Number(producto.pv_ini).toFixed(2)}€</strong></span>}
                  </div>
                  {producto.descripcion && <div style={{ fontSize: 10, opacity: 0.5, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{producto.descripcion}</div>}
                </div>
                <button onPointerDown={(e) => { e.preventDefault(); togglePantalla(); }} className="btn btn-outline"
                  style={{ fontSize: 10, padding: "3px 8px", whiteSpace: "nowrap", background: enPantalla ? "#16a34a" : undefined, color: enPantalla ? "#fff" : undefined, borderColor: enPantalla ? "#16a34a" : undefined }}>
                  {enPantalla ? "✅" : "📺"}
                </button>
              </div>
            </div>
          )}

          {/* Campos + numpad + botones */}
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 5 }}>
                {campos.map(c => <FieldCard key={c.id} label={c.label} value={c.value} suffix={c.suffix} active={campo === c.id} disabled={isActive} onActivate={() => setCampo(c.id)} />)}
              </div>
              <Pad onPress={press} integer={isInteger(campo)} />
            </div>
            {/* Botones + estado */}
            <div style={{ width: 100, display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
              <button className="btn btn-primary" style={{ width: "100%", padding: "14px 0", fontSize: 13, fontWeight: 800 }}
                onPointerDown={(e) => { e.preventDefault(); handleStart(); }} disabled={!liveUser || isActive}>
                {modo === "venta" ? "▶ VENTA" : "▶ SUBASTA"}
              </button>
              <button className="btn btn-outline" style={{ width: "100%", padding: "9px 0", fontSize: 12 }}
                onPointerDown={(e) => { e.preventDefault(); handleReset(); }}>↺ Reset</button>
              <div style={{ padding: "7px 8px", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", fontSize: 11 }}>
                <div style={{ fontWeight: 700, color: isActive ? "#16a34a" : "#999", fontSize: 12 }}>
                  {isActive ? "● EN VIVO" : "○ Parado"}
                </div>
                {modo === "venta" && isActive && <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 900 }}>{ventaRem}s</div>}
                {modo === "subasta" && isActive && <div style={{ fontVariantNumeric: "tabular-nums", fontSize: 22, fontWeight: 900 }}>{remSeg}s</div>}
                {modo === "subasta" && subastaState.top && (
                  <div style={{ marginTop: 3 }}>
                    <div style={{ opacity: 0.6, fontSize: 10 }}>Top puja</div>
                    <div style={{ fontWeight: 700 }}>{subastaState.top.amount}€</div>
                    <div style={{ opacity: 0.7, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 10 }}>{subastaState.top.nickname || subastaState.top.uniqueId}</div>
                  </div>
                )}
                <div style={{ opacity: 0.45, marginTop: 4, borderTop: "1px solid var(--border)", paddingTop: 4, lineHeight: 1.7, fontSize: 10 }}>
                  <div>Tab→campo</div><div>Enter→start</div><div>Esc→borrar</div>
                </div>
              </div>
            </div>
          </div>

          {/* Resultado venta */}
          {modo === "venta" && winners.length > 0 && (
            <div style={{ border: "2px solid var(--text)", borderRadius: "var(--radius)", padding: "8px 12px" }}>
              <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 5 }}>GANADORES ({winners.length}/{Number(ventaState?.qty) || winners.length})</div>
              {winners.map((w, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                  {w.profilePictureUrl && <img src={w.profilePictureUrl} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} />}
                  <span style={{ fontWeight: 700, fontSize: 13 }}>{w.nickname || w.uniqueId}</span>
                  <span style={{ marginLeft: "auto", fontWeight: 900, fontSize: 13, color: "var(--brand-red)" }}>{ventaState?.price != null ? `${Number(ventaState.price).toFixed(2)}€` : `${Number(precio).toFixed(2)}€`}</span>
                </div>
              ))}
            </div>
          )}

          {/* Resultado subasta */}
          {modo === "subasta" && subastaResult && (
            <div style={{ border: "2px solid var(--brand-red)", borderRadius: "var(--radius)", padding: "8px 12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 4 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "var(--brand-red)" }}>🏆 Subasta finalizada</div>
                {confirmStatus?.ok && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 11 }}>✅ #{confirmStatus.id}</span>
                    <button className="btn btn-outline" style={{ fontSize: 10, padding: "2px 7px" }} onClick={() => socket.current?.emit("print:reprint", { transactionId: confirmStatus.id })}>🖨️</button>
                  </div>
                )}
              </div>
              {(subastaResult.leaderboard || (subastaResult.winner ? [subastaResult.winner, ...(subastaResult.next5 || [])] : [])).map((b, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", borderRadius: 6, border: `1px solid ${i === 0 ? "var(--brand-red)" : "var(--border)"}`, background: i === 0 ? "#fff5f5" : "#fafafa", marginBottom: 5 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, width: 18, color: i === 0 ? "var(--brand-red)" : "#999" }}>#{i+1}</span>
                  {b.profilePictureUrl && <img src={b.profilePictureUrl} alt="" style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} />}
                  <span style={{ flex: 1, fontWeight: 700, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.nickname || b.uniqueId}</span>
                  <span style={{ fontWeight: 900, fontSize: 13, color: "var(--brand-red)" }}>{b.amount}€</span>
                  <button className="btn btn-primary" style={{ fontSize: 10, padding: "3px 8px", opacity: confirmStatus?.ok ? 0.4 : 1 }}
                    disabled={!!confirmStatus?.ok} onPointerDown={(e) => { e.preventDefault(); confirmarGanador(b); }}>
                    ✓
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Columna derecha: Chat ── */}
        <div className="directo-right">
          <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--border)", fontWeight: 700, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
            <span>💬 Chat</span>
            <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.5 }}>{messages.length} mensajes</span>
          </div>
          <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <div ref={chatRef}
              onScroll={() => {
                if (programmaticScrollRef.current) return;
                const el = chatRef.current;
                if (!el) return;
                const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
                if (nearBottom) {
                  if (!autoScrollRef.current) { autoScrollRef.current = true; setChatAutoScroll(true); setChatNewCount(0); }
                } else {
                  if (autoScrollRef.current) { autoScrollRef.current = false; setChatAutoScroll(false); }
                }
              }}
              style={{ flex: 1, overflowY: "auto", padding: "6px 8px" }}>
            {messages.map((m, i) => {
              const uid = String(m.userId || "");
              const isSaved = uid && savedIds.has(uid);
              return (
                <div key={i} style={{ display: "flex", gap: 7, padding: "5px 0", borderBottom: "1px solid #f3f3f3", alignItems: "flex-start" }}>
                  {m.profilePictureUrl
                    ? <img src={m.profilePictureUrl} alt="" style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0, marginTop: 1 }} />
                    : <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#f0f0f0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, opacity: 0.5 }}>👤</div>
                  }
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 4 }}>
                      <strong style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.nickname || m.uniqueId}</strong>
                      {uid && !isSaved && (
                        <button onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); setPopupMsg(m); }}
                          style={{ flexShrink: 0, fontSize: 10, padding: "2px 8px", background: "var(--brand-red)", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}>
                          + Añadir
                        </button>
                      )}
                      {uid && isSaved && (
                        <span style={{ flexShrink: 0, fontSize: 10, color: "#16a34a", fontWeight: 700 }}>✓</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.85, wordBreak: "break-word" }}>{m.comment}</div>
                  </div>
                </div>
              );
            })}
            </div>
            {/* Botón ir al último */}
            {!chatAutoScroll && (
              <div style={{ position: "absolute", bottom: 8, left: 0, right: 0, display: "flex", justifyContent: "center", pointerEvents: "none" }}>
                <button
                  onPointerDown={(e) => {
                    e.preventDefault();
                    const el = chatRef.current;
                    if (!el) return;
                    programmaticScrollRef.current = true;
                    el.scrollTop = el.scrollHeight;
                    requestAnimationFrame(() => { programmaticScrollRef.current = false; });
                    autoScrollRef.current = true;
                    setChatAutoScroll(true);
                    setChatNewCount(0);
                  }}
                  style={{
                    pointerEvents: "all", cursor: "pointer",
                    background: "var(--brand-red)", color: "#fff",
                    border: "none", borderRadius: 20, padding: "6px 16px",
                    fontSize: 12, fontWeight: 700, boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    touchAction: "manipulation",
                  }}>
                  ↓ Ir al último{chatNewCount > 0 ? ` (${chatNewCount})` : ""}
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
